import React, { useState, useEffect, Fragment } from "react";
import { useParams } from "react-router-dom";
import axiosInstance from "../axios";
import logo from "../../assets/images/dashboard/Logo Amr Khamis El Yousr.png";
import { Container, Row, Col, Table, Button, Card, CardBody } from "reactstrap";
import Breadcrumb from "../common/breadcrumb";

const Invoice = () => {
	const { orderId } = useParams();
	const [orderData, setOrderData] = useState(null);
	const [items, setItems] = useState([]);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		const fetchData = async () => {
			try {
				const [orderRes, itemsRes] = await Promise.all([
					axiosInstance.get(`/admin/order-detail/${orderId}`),
					axiosInstance.get(`/admin/order-detail/${orderId}/order-item-list`),
				]);
				setOrderData(orderRes.data);
				setItems(itemsRes.data);
				setLoading(false);
			} catch (error) {
				console.error("Error fetching invoice data", error);
				setLoading(false);
			}
		};
		fetchData();
	}, [orderId]);

	const handlePrint = () => {
		window.print();
	};

	if (loading) return <div className="text-center p-5">جاري التحميل...</div>;
	if (!orderData)
		return <div className="text-center p-5">لم يتم العثور على الطلب</div>;

	const billing = orderData.billing_detail?.[0] || {};

	return (
		<Fragment>
			<div className="no-print">
				<Breadcrumb title="الفاتورة" />
			</div>
			<Container>
				<div className="mb-4 no-print text-end">
					<Button color="primary" onClick={handlePrint}>
						<i className="fa fa-print me-2"></i> طباعة الفاتورة
					</Button>
				</div>

				<Card className="invoice-card shadow-sm mb-5">
					<CardBody className="p-5">
						{/* Header */}
						<Row className="mb-4 align-items-center">
							<Col md="6">
								<img src={logo} alt="Logo" style={{ maxHeight: "80px" }} />
							</Col>
							<Col md="6" className="text-md-end mt-3 mt-md-0">
								<h2 className="text-primary mb-1"> طلب</h2>
								<h5 className="text-muted">رقم الطلب: #{orderData.id}</h5>
							</Col>
						</Row>

						<hr className="my-4" />

						{/* Details Section */}
						<Row className="mb-5">
							<Col md="6">
								<h6 className="text-muted text-uppercase mb-3">بيانات العميل:</h6>
								<div className="invoice-details">
									<h5 className="mb-1">
										{billing.first_name} {billing.last_name}
									</h5>
									<p className="mb-1 text-muted">
										<i className="fa fa-phone me-1"></i> {billing.phone}
									</p>
									{billing.phone_2 && (
										<p className="mb-1 text-muted">
											<i className="fa fa-phone me-1"></i> {billing.phone_2}
										</p>
									)}
									<p className="mb-1 text-muted">
										<i className="fa fa-envelope me-1"></i> {billing.email}
									</p>
									<p className="mb-0 text-muted">
										<i className="fa fa-map-marker me-1"></i> {billing.full_address}
									</p>
								</div>
							</Col>
							<Col md="6" className="text-md-end mt-4 mt-md-0">
								<h6 className="text-muted text-uppercase mb-3">تفاصيل الفاتورة:</h6>
								<div className="invoice-details">
									<p className="mb-1">
										<strong>التاريخ:</strong> {orderData.created_at?.split("T")[0]}
									</p>
									<p className="mb-1">
										<strong>حالة الدفع:</strong>{" "}
										<span
											className={`badge ${orderData.payment_status === "paid" ? "bg-success" : "bg-warning text-dark"}`}>
											{orderData.payment_status}
										</span>
									</p>
									<p className="mb-0">
										<strong>طريقة الدفع:</strong>{" "}
										{orderData.payment_method?.name_ar ||
											orderData.payment_method ||
											"غير محدد"}
									</p>
								</div>
							</Col>
						</Row>

						{/* Table */}
						<div className="table-responsive mb-5">
							<Table bordered hover>
								<thead className="table-light">
									<tr>
										<th className="text-center">#</th>
										<th>المنتج</th>
										<th className="text-center">الكمية</th>
										<th className="text-center">سعر الوحدة</th>
										<th className="text-center">الإجمالي</th>
									</tr>
								</thead>
								<tbody>
									{items.map((item, index) => (
										<tr key={index}>
											<td className="text-center">{index + 1}</td>
											<td>
												{item.product_line?.product?.name_ar}
												<div className="small text-muted">
													{item.product_line?.color_ar}
												</div>
											</td>
											<td className="text-center">{item.quantity}</td>
											<td className="text-center">
												{item.total_price / item.quantity} ج.م
											</td>
											<td className="text-center">{item.total_price} ج.م</td>
										</tr>
									))}
								</tbody>
							</Table>
						</div>

						{/* Totals */}
						<Row>
							<Col md="6" className="mt-4 mt-md-0">
								{billing.note && (
									<div className="p-3 bg-light rounded">
										<h6 className="text-muted mb-2">ملاحظات:</h6>
										<p className="mb-0 small">{billing.note}</p>
									</div>
								)}
							</Col>
							<Col md="6">
								<div className="totals-section border rounded p-3">
									<div className="d-flex justify-content-between mb-2">
										<span className="text-muted">إجمالي المنتجات:</span>
										<span>{orderData.total_items_price} ج.م</span>
									</div>
									<div className="d-flex justify-content-between mb-2 text-success">
										<span>خصم الكوبون ({orderData.promo_code?.code || "بدون"}):</span>
										<span>- {orderData.promo_code_discount} ج.م</span>
									</div>
									<div className="d-flex justify-content-between mb-3 text-info">
										<span>
											تكلفة الشحن (
											{orderData.shipping_area?.name_ar ||
												orderData.shipping_area ||
												"غير محدد"}
											):
										</span>
										<span>+ {orderData.shipping_price} ج.م</span>
									</div>
									<hr />
									<div className="d-flex justify-content-between">
										<h4 className="mb-0 fw-bold">الإجمالي النهائي:</h4>
										<h4 className="mb-0 fw-bold text-primary">
											{orderData.total_price_after_discount} ج.م
										</h4>
									</div>
								</div>
							</Col>
						</Row>

						{/* Footer */}
						<div className="text-center mt-5 pt-3 border-top text-muted small">
							<p className="mb-1">شكراً لتعاملكم معنا!</p>
							<p className="mb-0">محلات اليسر للاقمشة</p>
						</div>
					</CardBody>
				</Card>
			</Container>

			<style>{`
				@media print {
					.no-print { display: none !important; }
					body { background-color: white !important; }
					.page-sidebar, .page-main-header, .footer { display: none !important; }
					.page-body { margin-left: 0 !important; margin-top: 0 !important; padding: 0 !important; }
					.container { width: 100% !important; max-width: 100% !important; border: none !important; }
					.card { border: none !important; box-shadow: none !important; }
					.card-body { padding: 0 !important; }
					.invoice-card { margin-bottom: 0 !important; }
					* { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
				}
				.invoice-card {
					border-radius: 15px;
					overflow: hidden;
				}
				.totals-section {
					background-color: #f8f9fa;
				}
				.order-badge {
					padding: 0.5em 1em;
					border-radius: 50px;
					font-size: 0.85em;
					font-weight: 600;
				}
				.status-paid { background-color: #d1e7dd; color: #0f5132; }
				.status-pending { background-color: #fff3cd; color: #664d03; }
			`}</style>
		</Fragment>
	);
};

export default Invoice;
